/**
 * 加盟店規約 (markdown) の表示 (#1657)。
 *
 * リポジトリに markdown レンダラが無い (顧客向け法務文書は plain text を
 * `whitespace-pre-wrap` で出している) ため、契約書に実際に出てくる記法だけを
 * 扱う最小の整形を置く。**依存を足さない**のが目的で、汎用 markdown 実装では
 * ないことに注意。
 *
 * 扱う記法: 見出し (#/##/###) / 引用 (>) / 箇条書き (- ・数字) / 強調 (**) /
 * リンク ([text](url)) / 表 (パイプ行はそのまま等幅で出す) / 水平線 (---)。
 *
 * 同意記録に残すハッシュは **API が配信した markdown 原文** に対するもので、
 * この整形結果に対するものではない。docx 版と同じく「原本の表現」であり、
 * 内容は同一。
 */

type Line = { key: string; el: React.ReactNode }

/** `**強調**` と `[text](url)` だけをインライン処理する。 */
function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  // リンクを先に落としてから強調を処理する (リンク文字列内の ** を壊さない)
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  const pushPlain = (s: string, k: string) => {
    if (!s) return
    // `**x**` → <strong>x</strong>
    s.split(/(\*\*[^*]+\*\*)/g).forEach((part, j) => {
      if (!part) return
      if (part.startsWith('**') && part.endsWith('**')) {
        out.push(
          <strong key={`${k}-b${j}`} className="font-semibold text-text-primary">
            {part.slice(2, -2)}
          </strong>,
        )
      } else {
        out.push(<span key={`${k}-t${j}`}>{part}</span>)
      }
    })
  }
  while ((m = linkRe.exec(text)) !== null) {
    pushPlain(text.slice(last, m.index), `${keyBase}-p${i}`)
    const [, label, href] = m
    out.push(
      href.startsWith('http') ? (
        <a
          key={`${keyBase}-a${i}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-navy underline"
        >
          {label}
        </a>
      ) : (
        // 相対リンク (docs 内の別ファイル) はアプリから辿れないので文字だけ残す
        <span key={`${keyBase}-a${i}`}>{label}</span>
      ),
    )
    last = m.index + m[0].length
    i += 1
  }
  pushPlain(text.slice(last), `${keyBase}-pz`)
  return out
}

export default function MerchantTermsBody({ text }: { text: string }) {
  const lines: Line[] = []
  const rawLines = text.split('\n')

  // 表は**連続するパイプ行をまとめて 1 つの横スクロール**に入れる。行ごとに
  // スクロール枠を作ると列が揃わず、改定履歴の表が読めなくなる。
  // `|---|---|` の区切り行は表示しない (罫線の意味しか持たない)。
  const tableRuns = new Map<number, string[]>()
  const consumed = new Set<number>()
  for (let i = 0; i < rawLines.length; i += 1) {
    if (consumed.has(i) || !rawLines[i].trim().startsWith('|')) continue
    const run: string[] = []
    let j = i
    while (j < rawLines.length && rawLines[j].trim().startsWith('|')) {
      if (!/^\|[\s|:-]+\|$/.test(rawLines[j].trim())) run.push(rawLines[j].trimEnd())
      consumed.add(j)
      j += 1
    }
    tableRuns.set(i, run)
  }

  rawLines.forEach((raw, idx) => {
    const key = `l${idx}`
    const line = raw.trimEnd()

    const run = tableRuns.get(idx)
    if (run) {
      // 1 行目を見出し、以降を本体として本物の <table> で出す。等幅テキストのままだと
      // パイプ記号が並ぶだけで、改定履歴や準用条項の対応表が読めない (#1710)。
      const rows = run.map((r) =>
        r.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()),
      )
      const [head, ...body] = rows
      lines.push({
        key,
        el: (
          <div className="my-2 overflow-x-auto">
            <table className="w-full border-collapse text-[12px] leading-relaxed">
              <thead>
                <tr>
                  {head.map((c, ci) => (
                    <th
                      key={`${key}-h${ci}`}
                      className="border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold text-text-primary"
                    >
                      {renderInline(c, `${key}-h${ci}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((cells, ri) => (
                  <tr key={`${key}-r${ri}`}>
                    {cells.map((c, ci) => (
                      <td
                        key={`${key}-r${ri}c${ci}`}
                        className="border border-gray-200 px-2 py-1 align-top text-text-secondary"
                      >
                        {renderInline(c, `${key}-r${ri}c${ci}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      })
      return
    }
    // run の 2 行目以降は上でまとめて出したので飛ばす
    if (consumed.has(idx)) return

    if (!line.trim()) {
      lines.push({ key, el: <div className="h-3" /> })
      return
    }
    if (/^---+$/.test(line.trim())) {
      lines.push({ key, el: <hr className="my-3 border-gray-200" /> })
      return
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    if (heading) {
      const level = heading[1].length
      const cls =
        level === 1
          ? 'mt-4 mb-2 font-heading text-lg font-bold text-text-primary'
          : level === 2
            ? 'mt-4 mb-1.5 font-heading text-base font-bold text-text-primary'
            : 'mt-3 mb-1 font-heading text-sm font-bold text-text-primary'
      // #1736 改定後のゲートが「該当の条項を見る」で飛ぶ先。見出しの文字 (空白なし) を持たせる
      lines.push({ key, el: <p className={cls} data-heading={heading[2].replace(/\s+/g, '')}>{renderInline(heading[2], key)}</p> })
      return
    }

    if (line.startsWith('> ')) {
      lines.push({
        key,
        el: (
          <p className="my-1 border-l-2 border-gold bg-gold/5 py-1 pl-3 text-[13px] leading-relaxed text-text-secondary">
            {renderInline(line.slice(2), key)}
          </p>
        ),
      })
      return
    }
    if (line.trim() === '>') {
      lines.push({ key, el: <div className="h-2" /> })
      return
    }

    const bullet = /^(\s*)[-*]\s+(.*)$/.exec(line)
    if (bullet) {
      const indent = bullet[1].length >= 2 ? 'ml-6' : 'ml-3'
      lines.push({
        key,
        el: (
          <p className={`${indent} text-[13px] leading-relaxed text-text-secondary`}>
            ・{renderInline(bullet[2], key)}
          </p>
        ),
      })
      return
    }

    const numbered = /^(\s*)(\d+)\.\s+(.*)$/.exec(line)
    if (numbered) {
      const indent = numbered[1].length >= 2 ? 'ml-6' : 'ml-3'
      lines.push({
        key,
        el: (
          <p className={`${indent} text-[13px] leading-relaxed text-text-secondary`}>
            {numbered[2]}. {renderInline(numbered[3], key)}
          </p>
        ),
      })
      return
    }

    lines.push({
      key,
      el: <p className="text-[13px] leading-relaxed text-text-secondary">{renderInline(line, key)}</p>,
    })
  })

  return (
    <div className="font-body">
      {lines.map(({ key, el }) => (
        <div key={key}>{el}</div>
      ))}
    </div>
  )
}
